import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AccountService } from '../../services/user/account.service';
import { ThemeService } from 'src/app/services/client/theme/theme.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { localStorageKeys } from 'src/app/services/localStorage/localStorageVariables';
import { NavController } from '@ionic/angular';
import { captureTravelverseSsoFromUrl, clearTravelverseSsoParamsFromUrl } from 'src/app/services/shared/travelverse-sso.util';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  accessList: string[];
  defaultRoute: string;
  clientCode: string;
  constructor(
    private accountService: AccountService,
    private router: Router,
    private themeService: ThemeService,
    private localStorageData: LocalStorageService,
    private navController: NavController
  ) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return (
      this.checkAuthentication(next, state) &&
      this.checkPartnerAuthorization(next, state)
    );
  }

  checkPartnerAuthorization(next, state): boolean {
    // Refactor below code to support private and public routes
    if (!this.clientCode && this.themeService.getPartner) {
      const partnerDetails = JSON.parse(
        this.localStorageData.getItem(localStorageKeys.PARTNER)
      );
      if (partnerDetails) {
        this.clientCode = partnerDetails.clientCode;
        this.accessList = partnerDetails.accessList;
        this.defaultRoute = partnerDetails.defaultRoute;
      }
    }
    if (this.accessList?.length > 0) {
      if (this.accessList.includes(next.routeConfig.path)) {
        return true;
      } else {
        // this.router.navigateByUrl(this.defaultRoute);
        if (
          (next.routeConfig.path === 'login' ||
            next.routeConfig.path === 'validator' ||
            next.routeConfig.path === 'registration' ||
            next.routeConfig.path === 'onboarding' ||
            next.routeConfig.path === 'redirecting') &&
          !this.accountService.isLoggedIn()
        ) {
          this.router.navigate(['error']);
        } else {
          const defaultSSOUrl = this.localStorageData.getItem(
            localStorageKeys.DEFAULT_SSO_URL
          );
          if (defaultSSOUrl) {
            this.navController.navigateRoot(defaultSSOUrl);
          } else {
            this.router.navigateByUrl(this.defaultRoute);
          }
        }
        return false;
      }
    } else {
      return true;
    }
  }

  checkAuthentication(next, state): boolean {
    const allowRegistration = this.accountService.isRegistrationAllowed();
    captureTravelverseSsoFromUrl(this.localStorageData, state.url);
    clearTravelverseSsoParamsFromUrl();
    if (this.accountService.isLoggedIn()) {
      this.setDefaultSsoUrl(state.url);
      // If logged in dont allow user to got to pre-auth pages
      if (this.accountService.isRegistered()) {
        if (
          next.routeConfig.path === 'login' ||
          next.routeConfig.path === 'validator' ||
          next.routeConfig.path === 'onboarding' ||
          (next.routeConfig.path === 'registration' && !allowRegistration)
        ) {
          this.router.navigate(['home']);
          return false;
        }

        // If flag is active and route is registration, allow access
        if (allowRegistration && next.routeConfig.path === 'registration') {
          return true;
        }
      } else {
        //if user loggedin restrict user to activate_membership page before registration page
        if (!this.accountService.isMember) {
          if (next.routeConfig.path === 'activate-membership') {
            return true;
          } else {
            this.router.navigate(['activate-membership']);
            return false;
          }
        }
        // If user is loggedin and not registered then force to register page
        if (next.routeConfig.path === 'registration') {
          return true;
        } else {
          this.router.navigate(['registration']);
          return false;
        }
      }
      //? Successfully logged in and registered
      if (this.accountService.redirectUrl) {
        const redirectUrl = this.accountService.redirectUrl;
        this.accountService.removeRedirectUrl();
        const defaultSSOUrl = this.localStorageData.getItem(
          localStorageKeys.DEFAULT_SSO_URL
        );
        if (defaultSSOUrl) {
          this.navController.navigateRoot(defaultSSOUrl);
        } else {
          this.router.navigateByUrl(redirectUrl);
        }
        return false;
      }
      return true;
    } else if (
      // If not logged in go to journey by default. If came in with url then only allow login/validator/journey
      (next.routeConfig.path === 'login' && !next?.queryParams?.client) ||
      next.routeConfig.path === 'validator' ||
      next.routeConfig.path === 'onboarding' ||
      next.routeConfig.path === 'redirecting'
    ) {
      return true;
    } else {
      //? When user is not signed in
      // add all public routes here in the condition
      const regex = /\/home\?token=[A-Za-z0-9-_.]+/;
      if (
        state.url !== '/home' &&
        state.url !== '/' &&
        !regex.test(state.url)
      ) {
        if (next?.queryParams?.client) {
          if (window.location.pathname !== '/login') {
            this.accountService.redirectUrl = window.location.pathname;
            // Store DEFAULT_SSO_URL when the URL contains a token
            this.setDefaultSsoUrl(state.url);
          }
        } else {
          this.accountService.redirectUrl = state.url;
          // Store DEFAULT_SSO_URL when the URL contains a token
          this.setDefaultSsoUrl(state.url);
        }
      }
      const ssoRegex = /\?token=[A-Za-z0-9-_.]+/;
      if (Capacitor.getPlatform() !== 'web') {
        this.router.navigate(['onboarding']);
      } else {
        // When guest login is enabled, route to redirecting to show loading
        if (this.themeService?.hasGuestLoginEnabled) {
          this.router.navigate(['redirecting']);
        } else if (ssoRegex.test(state.url)) {
          this.setDefaultSsoUrl(state.url);
          this.navController.navigateRoot(['redirecting']);
        } else {
          this.router.navigate(['login']);
        }
      }
      return false;
    }
  }

  setDefaultSsoUrl(sourceUrl: string): void {
    if (sourceUrl && sourceUrl.includes('token=')) {
      const parsed = new URL(sourceUrl, window.location.origin);
      parsed.searchParams.delete('token');
      const query = parsed.searchParams.toString();
      const cleanUrl = parsed.pathname + (query ? '?' + query : '');
      const partnerData = JSON.parse(
        localStorage.getItem(localStorageKeys.PARTNER) || '{}'
      );
      const defaultRoute = partnerData?.defaultRoute || 'home';
      if (parsed.pathname === '/home' && defaultRoute !== 'home') {
        this.localStorageData.setItem(
          localStorageKeys.DEFAULT_SSO_URL,
          defaultRoute
        );
      } else if (cleanUrl && cleanUrl !== '/') {
        this.localStorageData.setItem(
          localStorageKeys.DEFAULT_SSO_URL,
          cleanUrl
        );
      } else {
        this.localStorageData.setItem(
          localStorageKeys.DEFAULT_SSO_URL,
          defaultRoute
        );
      }
    }
  }
}
